import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"

import { ArrowRight } from "@/components/icons"
import { Button } from "@/components/ui/button"

import { useFairUseConfigForm } from "./fair-use-config-form-context"
import { FairUseConfigFormLoading } from "./fair-use-config-form-provider"
import { fairUseHasEntitlements } from "./fair-use-entitlements-utils"
import { FairUsePlansPanel } from "./fair-use-plans-panel"
import { fairUseHasImagePrices, fairUseHasVoiceTokenPrices } from "./fair-use-pricing-utils"
import { fairUsePlansUpdate } from "./fair-use-settings-utils"

/**
 * Plans & pricing — the platform's commercial envelope: what each plan is
 * worth per month, what a top-up adds and costs, what each plan is entitled to,
 * and what images and voice are priced at.
 *
 * Which MODELS a plan may use is not here and never was: that is a property of
 * the model, edited on the model itself under Configuration → Models. The
 * pointer below is a link, not a second editor — two places to set one value is
 * how they end up disagreeing.
 */
export function FairUseTabPlans() {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { data, dirty, form, isLoading, isPlatformAdmin, save, savingPanel, updateForm } =
    useFairUseConfigForm()

  if (isLoading || !form || !data) return <FairUseConfigFormLoading />

  // The admin workspace renders one area at a time, so the Models card is not
  // in the DOM to scroll to — the pointer has to change the route the same way
  // the section picker does.
  const goToModels = () => {
    const params = new URLSearchParams(searchParams)
    params.set("area", "configuration")
    params.set("setting", "models")
    params.delete("view")
    setSearchParams(params)
  }

  return (
    <div className="space-y-5">
      <FairUsePlansPanel
        canEdit={isPlatformAdmin}
        config={data.config}
        entitlementSchema={data.entitlement_schema}
        form={form}
        isDirty={dirty.plans}
        isSaving={savingPanel === "plans"}
        onChange={(patch) => updateForm("plans", patch)}
        onSave={() =>
          save(
            "plans",
            fairUsePlansUpdate(form, {
              includeEntitlements: fairUseHasEntitlements(data.config),
              includeImagePrices: fairUseHasImagePrices(data.config),
              includeVoiceTokenPrices: fairUseHasVoiceTokenPrices(data.config),
              schema: data.entitlement_schema,
            })
          )
        }
      />

      {isPlatformAdmin ? (
        <div className="space-y-2 rounded-lg border p-4" data-testid="fair-use-model-plans-pointer">
          <p className="text-sm font-semibold">
            {t(
              "adminDashboard.fairUse.settings.plans.modelPlansTitle",
              "Which models each plan may use"
            )}
          </p>
          <p className="text-muted-foreground max-w-prose text-sm">
            {t(
              "adminDashboard.fairUse.settings.plans.modelPlansBody",
              "That is set on the model itself, so a new model is reachable the moment it is added: open Configuration → Model Management and pick the plans there."
            )}
          </p>
          <Button className="px-0" size="sm" variant="link" onClick={goToModels}>
            {t("adminDashboard.fairUse.settings.plans.modelPlansAction", "Go to Model Management")}
            <ArrowRight aria-hidden="true" className="ml-1 size-4" />
          </Button>
        </div>
      ) : null}
    </div>
  )
}
